import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import {
  Avatar,
  Box,
  Button,
  CardInfo,
  Grid,
  Icon,
  Text,
  Title,
} from '@factorialco/deprecated-design-system'
import {
  ApproveContractVersionRequestDocument,
  ApprovePromotionPageContractVersionChangesFragment,
  ApprovePromotionPageContractVersionFragment,
  ApprovePromotionPageJobCatalogTreeLevelNodeFragment,
  ContractVersionsPageDocument,
  DeleteContractVersionRequestDocument,
  RejectContractVersionRequestDocument,
} from 'generated/resources/graphql'

import HelpCenterLink from 'modules/core/components/HelpCenterLink'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import buildUrl from 'modules/core/lib/buildUrl'
import { dateBackendToUI } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import ApprovalsInformationBlock from 'modules/approvals/components/ApprovalsInformationBlock'
import { FLOWS } from 'modules/contracts/components/FlowSelectorModal/constants'
import { mapUpdate } from 'modules/contracts/lib/ContractHistoryUtils'
import getIgnoredKeys from 'modules/contracts/lib/ignoredKeys'
import CardHeader from 'modules/contracts/pages/ApprovePromotionPage/_CardHeader'
import CardRow from 'modules/contracts/pages/ApprovePromotionPage/_CardRow'

import ConfirmDeleteRequestModal from './_ConfirmDeleteRequestModal'
import ConfirmRejectModal from './_ConfirmRejectModal'

type Props = {
  contractVersionRequest: ApprovePromotionPageContractVersionFragment
  contractVersionChanges: ApprovePromotionPageContractVersionChangesFragment
  backPath: string
  hasDevJobCatalogTree: boolean
  hasTimeTrackingAnnualisationDailyValue: boolean
  hasDevTimeTrackingWorkableDaysDefinition: boolean
  jobCatalogTreeLevelNodes: ApprovePromotionPageJobCatalogTreeLevelNodeFragment[]
}

const Content = ({
  contractVersionRequest,
  contractVersionChanges,
  backPath,
  hasDevJobCatalogTree,
  hasTimeTrackingAnnualisationDailyValue,
  hasDevTimeTrackingWorkableDaysDefinition,
  jobCatalogTreeLevelNodes,
}: Props) => {
  const navigate = useNavigate()

  const [approveRequestMutation] = useMutation(ApproveContractVersionRequestDocument, {
    extractorFn: (data) => data.contractsMutations.approveContractsContractVersionRequest,
    refetchQueries: [ContractVersionsPageDocument],
    awaitRefetchQueries: true,
  })

  const [rejectRequestMutation] = useMutation(RejectContractVersionRequestDocument, {
    extractorFn: (data) => data.contractsMutations.rejectContractsContractVersionRequest,
    refetchQueries: [ContractVersionsPageDocument],
    awaitRefetchQueries: true,
  })

  const [deleteRequestMutation] = useMutation(DeleteContractVersionRequestDocument, {
    extractorFn: (data) => data.contractsMutations.deleteContractsContractVersionRequest,
    refetchQueries: [ContractVersionsPageDocument],
    awaitRefetchQueries: true,
  })

  const approveRequest = async () => {
    const response = await approveRequestMutation({
      variables: {
        requestId: contractVersionRequest.id ?? '0',
      },
    })

    const handled = handleResult(response, () => {})

    if (handled.success) {
      track(trackingEvents.employee.contracts.approveContractRequest, {
        request_type: FLOWS.promotion,
        request_date: contractVersionRequest.approvalRequestCreatedAt,
        request_reason: contractVersionRequest.requestDetails,
      })
      navigate(backPath)
    }
  }

  const rejectRequest = async (reason?: string) => {
    const response = await rejectRequestMutation({
      variables: {
        requestId: contractVersionRequest.id ?? '0',
        reason: reason,
      },
    })

    const handled = handleResult(response, () => {})

    if (handled.success) {
      track(trackingEvents.employee.contracts.rejectContractRequest, {
        request_type: FLOWS.promotion,
        request_date: contractVersionRequest.approvalRequestCreatedAt,
        request_reason: contractVersionRequest.requestDetails,
      })
      navigate(backPath)
    }
  }

  const deleteRequest = async () => {
    const response = await deleteRequestMutation({
      variables: {
        requestId: contractVersionRequest.id ?? '0',
      },
      refetchQueries: [ContractVersionsPageDocument],
      awaitRefetchQueries: true,
    })

    const handled = handleResult(response, () => {})

    if (handled.success) {
      navigate(backPath)
    }
  }

  const {
    access: { id: currentAccessId },
  } = useCurrentSession()
  const requesterId = contractVersionRequest.approvalAuthor?.id
  const requester = contractVersionRequest.approvalAuthor
  const requestedAt = contractVersionRequest.approvalRequestCreatedAt || ''
  const requestEffectiveOnDate = contractVersionRequest?.effectiveOn
  const approvers_ids = contractVersionRequest.approversIds || []
  const canAcceptOrReject = approvers_ids.includes(currentAccessId)
  const canDeleteRequest = currentAccessId == requesterId
  const requesterName = requester?.fullName || ''
  const employeeName = contractVersionRequest.employee?.fullName || ''

  const ignoredKeys = getIgnoredKeys(
    contractVersionRequest?.country || '',
    hasDevJobCatalogTree,
    hasTimeTrackingAnnualisationDailyValue,
    hasDevTimeTrackingWorkableDaysDefinition
  )
  const filteredContractVersionUpdates = contractVersionChanges.updates.filter(
    (update) => !ignoredKeys.includes(update.field)
  )

  const [showConfirmRejectModal, setShowConfirmRejectModal] = React.useState(false)
  const [showConfirmDeleteRequestModal, setShowConfirmDeleteRequestModal] = React.useState(false)

  const confirmReject = (reason?: string) => {
    rejectRequest(reason)
    setShowConfirmRejectModal(false)
  }
  const onClickReject = () => {
    setShowConfirmRejectModal(true)
  }

  const confirmDeleteRequest = () => {
    deleteRequest()
    setShowConfirmDeleteRequestModal(false)
  }

  const onClickDelete = () => {
    setShowConfirmDeleteRequestModal(true)
  }

  const requestDetails = contractVersionRequest.requestDetails
  const actionType = contractVersionRequest.approvalActionType || ''

  let title = i18n.t('contract_versions.approval_page.promotion.title', {
    requesterName,
    employeeName,
  })
  let reasonTitle = i18n.t('contract_versions.approval_page.promotion.reason_title')

  if (actionType === FLOWS.evolution) {
    title = i18n.t('contract_versions.approval_page.evolution.title', {
      requesterName,
      employeeName,
    })
    reasonTitle = i18n.t('contract_versions.approval_page.evolution.reason_title')
  }

  const formattedChanges = filteredContractVersionUpdates
    .map((update) =>
      mapUpdate(
        update,
        filteredContractVersionUpdates,
        contractVersionRequest.employee?.legalEntity?.id ?? '0',
        jobCatalogTreeLevelNodes
      )
    )
    .filter((u): u is NonNullable<typeof u> => Boolean(u))

  return (
    <Box width="full">
      <Grid variant="fixed">
        <Box gridColSpan={{ xxs: 12, xs: 8, s: 6 }} gap="s12">
          <Avatar size="s24" url={buildUrl(requester?.avatar?.url)} label={requesterName} />
          <Title title={title} />
          <Box flexDirection="row" alignItems="center" gap="s8">
            <Icon.Small icon="clock" />
            <Text size="200">
              {i18n.t('contract_versions.approval_page.date_label', {
                date: dateBackendToUI(requestedAt),
              })}
            </Text>
          </Box>
          {requestDetails && (
            <>
              <Box paddingTop="s16">
                <Text size="100" caps weight="semibold">
                  {reasonTitle}
                </Text>
              </Box>
              <Text size="200">{requestDetails}</Text>
            </>
          )}
          {canDeleteRequest && (
            <Box paddingTop="s16">
              <Button type="secondary" onClick={() => onClickDelete()} iconName="delete">
                {i18n.t('contract_versions.approval_page.delete_request')}
              </Button>
            </Box>
          )}
          <Box gap="s64">
            <Box paddingTop="s32">
              {contractVersionRequest.id && (
                <ApprovalsInformationBlock
                  resourceId={contractVersionRequest.id}
                  resourceType="Contracts::ContractVersion"
                />
              )}
            </Box>
            <HelpCenterLink name="about-contracts-approval-system">
              {i18n.t('contract_versions.approval_page.help_center')}
            </HelpCenterLink>
          </Box>
        </Box>
        <Box
          gridColSpan={{ xxs: 12, xs: 4, s: 6 }}
          justifyContent="flexStart"
          alignItems="flexStart"
          width="full"
        >
          <CardInfo
            header={
              <CardHeader
                canApproveOrReject={canAcceptOrReject}
                requestStatus={contractVersionRequest.status}
                actionType={actionType}
                onClickApprove={approveRequest}
                onClickReject={onClickReject}
              />
            }
          >
            <Box gap="s12">
              <Text size="200">
                {i18n.t('contract_versions.approval_page.promotion.card_info.effective_date')}
              </Text>
              <Box
                flexDirection="row"
                gap="s12"
                borderRadius={{ all: 'abs008' }}
                background="grey100"
                border={{ all: { width: 's1', style: 'solid', color: 'grey400' } }}
              >
                <Box padding="s12">{dateBackendToUI(requestEffectiveOnDate)}</Box>
              </Box>
              {formattedChanges.map(
                (update, index) =>
                  update && <CardRow key={update.field + '-' + index} update={update} />
              )}
            </Box>
          </CardInfo>
        </Box>
      </Grid>
      {showConfirmRejectModal && (
        <ConfirmRejectModal
          onClose={() => setShowConfirmRejectModal(false)}
          onSubmit={(reason) => confirmReject(reason)}
          authorFullName={requesterName}
          targetFullName={employeeName}
        />
      )}
      {showConfirmDeleteRequestModal && (
        <ConfirmDeleteRequestModal
          onClose={() => setShowConfirmDeleteRequestModal(false)}
          onSubmit={() => confirmDeleteRequest()}
          targetFullName={employeeName}
          actionType={actionType}
        />
      )}
    </Box>
  )
}

export default Content
